import {
  computeAccruedAllowance,
  computeAnnualAllowance,
  formatAllowanceBalance,
} from "../Components/Kronaly/Vacaciones/vacationPolicyHelpers";

/**
 * Normaliza un empleado del API (`GET /v1/employees`) al shape que consume
 * la vista de Empleados y su drawer.
 *
 * @param api  Objeto crudo del endpoint /employees.
 * @param ctx  Contexto opcional con datos cruzados:
 *   - activeContract: respuesta de /employees/{id}/contracts/active (objeto o null).
 *   - schedulesById: mapa { id → workSchedule } para resolver nombres de horario.
 *   - policiesById: mapa { id → vacationPolicy } para resolver el saldo de vacaciones.
 *   - todayStatus: { is_active, total_seconds, entries_count } para el estado de hoy.
 *   - todayDow: clave de día (mon/tue/...) para distinguir laborable vs no laborable.
 */
const MONTHS_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const formatStartDate = (ymd) => {
  if (!ymd) return null;
  const [y, m] = String(ymd).split("-").map(Number);
  if (!y || !m) return ymd;
  return `${MONTHS_ES[m - 1]} ${y}`;
};

const formatHours = (seconds) => {
  if (!seconds || seconds < 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} h`;
};

const DOW_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const isWorkingDay = (schedule, todayDow) => {
  if (!schedule || !todayDow) return null;
  const day = schedule.config?.days?.[todayDow];
  if (!day) return false;
  return (day.total_minutes ?? 0) > 0;
};

const computeTodayState = (status, schedule, todayDow, hasContract) => {
  if (status?.is_active) {
    return { state: "working", time: formatHours(status.total_seconds) };
  }
  if (status?.entries_count > 0) {
    return { state: "finished", time: formatHours(status.total_seconds) };
  }
  // Sin entradas hoy. Distinguimos según schedule:
  if (!hasContract) return { state: "off", time: null };
  const working = isWorkingDay(schedule, todayDow);
  if (working === true) return { state: "missed", time: null };
  if (working === false) return { state: "non-working", time: null };
  return { state: "off", time: null };
};

export const todayDowKey = () => DOW_KEYS[new Date().getDay()];

export function mapApiEmployeeToUi(api, ctx = {}) {
  const first = (api?.first_name || "").trim();
  const last = (api?.last_name || "").trim();
  const name = [first, last].filter(Boolean).join(" ") || "Sin nombre";

  const ac = ctx.activeContract || null;
  const contract = ac?.contract || null;
  const terms = ac?.current_terms || null;
  const schedule = terms?.work_schedule_id
    ? ctx.schedulesById?.[terms.work_schedule_id] || null
    : null;

  let contractLabel = "Sin contrato";
  if (contract) {
    contractLabel = contract.end_date ? "Temporal" : "Indefinido";
  }

  let scheduleLabel = "—";
  if (schedule) {
    if (schedule.weekly_minutes) {
      const hrs = Math.round((schedule.weekly_minutes / 60) * 10) / 10;
      scheduleLabel = `${hrs} h/sem`;
    } else if (schedule.name) {
      scheduleLabel = schedule.name;
    }
  }

  const today = computeTodayState(
    ctx.todayStatus,
    schedule,
    ctx.todayDow,
    Boolean(contract),
  );

  const policy = terms?.vacation_policy_id
    ? ctx.policiesById?.[terms.vacation_policy_id] || null
    : null;
  const accrued = policy ? computeAccruedAllowance(policy, contract?.start_date) : null;
  const annual = policy ? computeAnnualAllowance(policy, contract?.start_date) : null;
  const balance = policy ? formatAllowanceBalance(accrued, policy) : "—";
  const balanceAnnual = policy ? formatAllowanceBalance(annual, policy) : "—";
  // Prorrateado/parcial: lo devengado es menor que el derecho anual.
  const balanceProrated = Number.isFinite(accrued) && Number.isFinite(annual)
    && Number(annual) > Number(accrued) + 0.01;

  return {
    id: api?.id,
    membershipId: api?.workspace_membership_id || null,
    status: api?.status || "active",
    name,
    role: terms?.job_title || api?.position || "—",
    team: "—",
    email: api?.email || null,
    phone: api?.phone || null,
    avatarPath: api?.avatar_path || null,
    hasContract: Boolean(contract),
    contract: contractLabel,
    schedule: scheduleLabel,
    state: today.state,
    time: today.time,
    balance,
    balanceAnnual,
    balanceProrated,
    since: contract?.start_date ? formatStartDate(contract.start_date) : "—",
    terminatedAt: api?.terminated_at || null,
  };
}
